import type { DefineDiagnosticDefinitionInput } from "@repo/api/schemas";
import type { DiagnosticDefinitionDto as DiagnosticDefinition } from "@repo/data-store";
import { useMemo } from "react";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import {
  type ClinicalDefinitionFormValues,
  clinicalDefinitionFormSchema,
  clinicalDefinitionToDefaults,
  clinicalDefinitionValuesToInput,
  type DefinitionMeta,
  EMPTY_CLINICAL_DEFINITION,
} from "./clinical-definition-form";

export const diagnosticDefinitionFormSchema = clinicalDefinitionFormSchema.extend({
  standardObservations: z.array(z.enum(["tolerance"])),
  observations: z
    .array(clinicalDefinitionFormSchema.shape.observations.element)
    .min(1, "A diagnostic requires at least one observation"),
});

export type DiagnosticDefinitionFormValues = ClinicalDefinitionFormValues &
  Pick<z.infer<typeof diagnosticDefinitionFormSchema>, "standardObservations">;

export const EMPTY_DIAGNOSTIC_DEFINITION: DiagnosticDefinitionFormValues = {
  ...structuredClone(EMPTY_CLINICAL_DEFINITION),
  standardObservations: ["tolerance"],
  observations: [],
};

export function diagnosticDefinitionToDefaults(
  definition?: DiagnosticDefinition | null,
): DiagnosticDefinitionFormValues {
  return {
    standardObservations: [...(definition?.standardObservations ?? ["tolerance"])],
    ...clinicalDefinitionToDefaults(
      definition
        ? {
            ...definition,
            deviceRequirements: definition.requirements.devices.map((requirement) => ({
              id: requirement.id,
              definition: requirement.definitionUrl,
            })),
            ...(definition.requirements.locationType === undefined
              ? {}
              : { locationRequirement: definition.requirements.locationType }),
          }
        : definition,
      EMPTY_DIAGNOSTIC_DEFINITION,
    ),
  };
}

export function diagnosticDefinitionValuesToInput(
  meta: DefinitionMeta,
  values: DiagnosticDefinitionFormValues,
): Omit<DefineDiagnosticDefinitionInput, "definitionUrl"> {
  return {
    name: meta.name,
    ...(meta.description.trim() ? { description: meta.description } : {}),
    ...clinicalDefinitionValuesToInput(values),
    standardObservations: values.standardObservations,
  };
}

export function useDiagnosticDefinitionForm(definition: DiagnosticDefinition | null) {
  const defaultValues = useMemo(() => diagnosticDefinitionToDefaults(definition), [definition]);
  return useAppForm({
    defaultValues,
    validators: { onDynamic: diagnosticDefinitionFormSchema },
    onSubmit: () => {},
    onSubmitInvalid: focusFirstInvalid,
  });
}

export type DiagnosticDefinitionForm = ReturnType<typeof useDiagnosticDefinitionForm>;
